import React, { useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import { ArrowBack as ArrowBackIcon, Book as BookIcon, Person as PersonIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Divider, Link,
    Typography
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import OpenLibraryService from "./OpenLibraryService";

const OpenLibraryDetail = () => {
    const [book, setBook] = useState(null);
    const [bookDetails, setBookDetails] = useState(null);
    const [bookContent, setBookContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const bookKey = location.pathname.replace('/admin/system/integrate/openlibrary/detail/', '');

    useEffect(() => {
        if (bookKey) {
            Logger.log("useEffect triggered with bookKey:", bookKey);
            fetchBookDetails();
        } else {
            Logger.log("No bookKey provided");
        }
    }, [bookKey]);

    const fetchBookDetails = async () => {
        setLoading(true);
        setError(null);

        try {
            Logger.log("Fetching book details for key:", bookKey);
            
            // First get the work details
            const workDetails = await OpenLibraryService.getBookDetails(bookKey);
            Logger.log("Work details:", workDetails); // Debug log
            setBookDetails(workDetails);
            
            // Create a book object from the work details for display
            const bookObj = {
                key: bookKey,
                title: workDetails.title || "Unknown Title",
                author_name: workDetails.authors ? workDetails.authors.map(author => {
                    if (typeof author === 'string') return author;
                    return author.name || author.author?.key || author;
                }) : [],
                first_publish_year: workDetails.first_publish_date || workDetails.publish_date,
                cover_id: workDetails.covers ? workDetails.covers[0] : null,
                cover_i: workDetails.covers ? workDetails.covers[0] : null, // Alternative cover field
                subject: workDetails.subjects || [],
                publisher: workDetails.publishers || [],
                language: workDetails.languages || []
            };
            
            Logger.log("Book object:", bookObj); // Debug log
            setBook(bookObj);
            
            // Try to fetch book content
            await fetchBookContent(bookKey);
        } catch (err) {
            Logger.error("Book details API Error:", err);
            Logger.error("Error details:", {
                message: err.message,
                stack: err.stack,
                bookKey: bookKey
            });
            setError(`Failed to fetch book details: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchBookContent = async (workKey) => {
        setContentLoading(true);
        setBookContent(null);

        try {
            // Try to get editions first to find a readable edition
            const editions = await OpenLibraryService.getWorkEditions(workKey, 10);
            
            if (editions.entries && editions.entries.length > 0) {
                // Look for editions with text content
                for (const edition of editions.entries) {
                    if (edition.key) {
                        try {
                            // Try to fetch the edition details
                            const editionDetails = await OpenLibraryService.getBookDetails(edition.key);
                            
                            // Check if this edition has text content
                            if (editionDetails && editionDetails.text) {
                                setBookContent({
                                    text: editionDetails.text,
                                    edition: editionDetails,
                                    source: 'edition'
                                });
                                break;
                            }
                        } catch (editionErr) {
                            Logger.warn(`Failed to fetch edition ${edition.key}:`, editionErr);
                            continue;
                        }
                    }
                }
            }

            // If no text content found in editions, try to get it from the work itself
            if (!bookContent) {
                try {
                    const workDetails = await OpenLibraryService.getBookDetails(workKey);
                    if (workDetails && workDetails.text) {
                        setBookContent({
                            text: workDetails.text,
                            edition: workDetails,
                            source: 'work'
                        });
                    }
                } catch (workErr) {
                    Logger.warn("No text content available for this work:", workErr);
                }
            }

        } catch (err) {
            Logger.error("Book content fetch error:", err);
            // Don't show error to user as content is optional
        } finally {
            setContentLoading(false);
        }
    };

    const formatAuthors = (authors) => {
        if (!authors || authors.length === 0) return "Unknown Author";
        return authors.map(author => {
            if (typeof author === 'string') return author;
            return author.name || author.author?.key || author;
        }).join(", ");
    };

    const formatPublishYear = (publishYear) => {
        if (!publishYear) return "Unknown Year";
        if (typeof publishYear === 'string') {
            // Extract year from date string
            const yearMatch = publishYear.match(/\d{4}/);
            return yearMatch ? yearMatch[0] : publishYear;
        }
        return publishYear;
    };

    const formatSubjects = (subjects) => {
        if (!subjects || subjects.length === 0) return [];
        return subjects.slice(0, 10); // Limit to 10 subjects
    };

    if (loading) {
        return (
            <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
                    <CircularProgress size={32} />
                    <Typography variant="h6" color="text.secondary">
                        Loading book details...
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Debug: Loading book with key: {bookKey}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    if (!book || !bookDetails) {
        return (
            <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Book not found
                </Alert>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Debug info: bookKey = {bookKey}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Book object: {JSON.stringify(book, null, 2)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Book details: {JSON.stringify(bookDetails, null, 2)}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
            {/* Back Button */}
            <Box sx={{ mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back to Search
                </Button>
            </Box>

            {/* Debug Info */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem' }}>
                    Debug - Book Key: {bookKey}
                    {'\n'}Book Object: {JSON.stringify(book, null, 2)}
                    {'\n'}Book Details Keys: {bookDetails ? Object.keys(bookDetails).join(', ') : 'null'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button 
                        variant="outlined" 
                        size="small"
                        onClick={async () => {
                            try {
                                Logger.log("Testing API call...");
                                const testUrl = `/openlibrary${bookKey}.json`;
                                Logger.log("Test URL:", testUrl);
                                const response = await fetch(testUrl);
                                Logger.log("Response status:", response.status);
                                const data = await response.json();
                                Logger.log("Test response:", data);
                            } catch (err) {
                                Logger.error("Test API error:", err);
                            }
                        }}
                    >
                        Test API Call
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={async () => {
                            try {
                                Logger.log("Testing direct API call...");
                                const testUrl = `https://openlibrary.org${bookKey}.json`;
                                Logger.log("Direct test URL:", testUrl);
                                const response = await fetch(testUrl);
                                Logger.log("Direct response status:", response.status);
                                const data = await response.json();
                                Logger.log("Direct test response:", data);
                            } catch (err) {
                                Logger.error("Direct test API error:", err);
                            }
                        }}
                    >
                        Test Direct API
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={async () => {
                            try {
                                Logger.log("Testing proxy health...");
                                const response = await fetch('/proxy-test');
                                const data = await response.json();
                                Logger.log("Proxy health check:", data);
                            } catch (err) {
                                Logger.error("Proxy health check error:", err);
                            }
                        }}
                    >
                        Test Proxy Health
                    </Button>
                </Box>
            </Box>

            {/* Book Details */}
            <Card>
                <CardContent>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {book.title}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                        <CardMedia
                            component="img"
                            sx={{ width: 300, height: 450, objectFit: "cover" }}
                            image={OpenLibraryService.getBookCover(book.cover_id || book.cover_i) || '/images/no-cover.png'}
                            alt={book.title}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" gutterBottom>
                                {book.title}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                <PersonIcon sx={{ fontSize: 20, mr: 0.5, verticalAlign: "middle" }} />
                                {formatAuthors(book.author_name)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Published: {formatPublishYear(book.first_publish_year)}
                            </Typography>
                            {book.publisher && book.publisher.length > 0 && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Publisher: {Array.isArray(book.publisher) ? book.publisher[0] : book.publisher}
                                </Typography>
                            )}
                            {book.language && book.language.length > 0 && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Language: {Array.isArray(book.language) ? book.language.join(", ") : book.language}
                                </Typography>
                            )}
                            {book.subject && book.subject.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Subjects:
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {formatSubjects(book.subject).map((subject, index) => (
                                            <Chip
                                                key={index}
                                                label={subject}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Book Description */}
                    {bookDetails.description && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Description
                            </Typography>
                            <Typography variant="body1">
                                {typeof bookDetails.description === 'string' 
                                    ? bookDetails.description 
                                    : bookDetails.description.value || "No description available"}
                            </Typography>
                        </Box>
                    )}

                    {/* Book Content */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            📖 Book Content
                            {contentLoading && <CircularProgress size={20} />}
                        </Typography>
                        
                        {contentLoading && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading book content...
                                </Typography>
                            </Box>
                        )}

                        {bookContent && bookContent.text && !contentLoading && (
                            <Box sx={{ 
                                border: "1px solid #e0e0e0", 
                                borderRadius: 1, 
                                p: 3, 
                                backgroundColor: "#fafafa",
                                maxHeight: "600px",
                                overflow: "auto",
                                fontFamily: "monospace",
                                fontSize: "0.9rem",
                                lineHeight: 1.6
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
                                    Content source: {bookContent.source === 'edition' ? 'Specific Edition' : 'Work'}
                                </Typography>
                                <Box sx={{ whiteSpace: "pre-wrap" }}>
                                    {bookContent.text}
                                </Box>
                            </Box>
                        )}

                        {!bookContent && !contentLoading && (
                            <Box sx={{ 
                                border: "1px solid #e0e0e0", 
                                borderRadius: 1, 
                                p: 3, 
                                backgroundColor: "#f5f5f5",
                                textAlign: "center"
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    📚 No text content available for this book
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    This book may not have full text available in Open Library
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* External Links */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            External Links:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Link 
                                href={`https://openlibrary.org${book.key}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                            >
                                <Button variant="outlined" size="small" startIcon={<BookIcon />}>
                                    View on Open Library
                                </Button>
                            </Link>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default OpenLibraryDetail;
